import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { updateUI } from 'redux/actions/ui';
import { getUser } from 'redux/selectors';
import buildAuthorization from 'common/utils/buildAuthorization';
import settings from 'common/configs/setting';

const getApiBaseUrl = () => {
    const stored = localStorage.getItem('API_URL') || '';
    return stored.replace(/\/+$/, '') + settings.api;
};

const POLL_INTERVAL_MS = 3000;

const useImportJobsMonitor = () => {
    const dispatch = useDispatch();
    const user = useSelector(getUser);
    const authHeader = buildAuthorization(user);

    const gmailIntervalRef = useRef(null);
    const bulkImageIntervalRef = useRef(null);

    useEffect(() => {
        if (!authHeader) {
            return;
        }

        const checkGmailJob = async () => {
            const storedStr = localStorage.getItem('activeGmailJob');
            if (!storedStr) {
                return;
            }

            try {
                const storedJob = JSON.parse(storedStr);
                const { jobId, journalId } = storedJob;

                const statusUrl = `${getApiBaseUrl()}/journal/${journalId}/email-imports/${jobId}/`;
                const res = await fetch(statusUrl, {
                    method: 'GET',
                    headers: { Authorization: authHeader }
                });

                if (!res.ok) {
                    if (res.status === 404) {
                        return;
                    }
                    throw new Error(`Poll failed (HTTP ${res.status})`);
                }

                const data = await res.json();
                const st = (data.status || '').toUpperCase();

                if (st === 'DONE' || st === 'FAILED') {
                    if (gmailIntervalRef.current) {
                        clearInterval(gmailIntervalRef.current);
                    }

                    const result = {
                        jobId,
                        journalId,
                        success:
                            data.success_count ?? data.emails_imported ?? 0,
                        skipped: data.skipped_count ?? 0,
                        failed: data.failed_count ?? 0,
                        isError: st === 'FAILED',
                        errorDetails: data.error_details || '',
                        skippedReasons: data.skipped_reasons || '[]',
                        failedReasons: data.failed_reasons || '[]',
                        restored: true
                    };

                    dispatch(
                        updateUI({
                            gmailImportInProgress: false,
                            gmailJobResult: result
                        })
                    );
                } else if (st === 'PROCESSING') {
                    dispatch(updateUI({ gmailImportInProgress: true }));
                }
            } catch (err) {
                if (gmailIntervalRef.current) {
                    clearInterval(gmailIntervalRef.current);
                }
                console.error('Error fetching gmail job state:', err);
                // Can optionally dispatch an error or wait
            }
        };

        const checkBulkImageJob = async () => {
            const storedStr = localStorage.getItem('activeBulkImageJob');
            if (!storedStr) {
                return;
            }

            try {
                const storedJob = JSON.parse(storedStr);
                const { jobId, journalId } = storedJob;

                const statusUrl = `${getApiBaseUrl()}/zip_photo_import_job/${jobId}/`;
                const res = await fetch(statusUrl, {
                    method: 'GET',
                    headers: { Authorization: authHeader }
                });

                if (!res.ok) {
                    if (res.status === 404) {
                        return;
                    }
                    throw new Error(`Poll failed (HTTP ${res.status})`);
                }

                const data = await res.json();
                const st = (data.status || '').toUpperCase();

                if (st === 'DONE' || st === 'SUCCESS' || st === 'FAILED') {
                    if (bulkImageIntervalRef.current) {
                        clearInterval(bulkImageIntervalRef.current);
                    }

                    const result = {
                        jobId,
                        journalId,
                        success: data.success_count ?? 0,
                        skipped: data.skipped_count ?? 0,
                        failed: data.failed_count ?? 0,
                        total: data.total_count ?? 0,
                        isError: st === 'FAILED',
                        errorDetails: data.error_details || '',
                        skippedReasons: data.skipped_reasons || '[]',
                        failedReasons: data.failed_reasons || '[]',
                        restored: true
                    };

                    dispatch(
                        updateUI({
                            bulkImageImportInProgress: false,
                            bulkImageJobResult: result
                        })
                    );
                } else if (st === 'PROCESSING') {
                    dispatch(updateUI({ bulkImageImportInProgress: true }));
                }
            } catch (err) {
                if (bulkImageIntervalRef.current) {
                    clearInterval(bulkImageIntervalRef.current);
                }
                console.error('Error fetching bulk image job state:', err);
            }
        };

        // Initial check
        checkGmailJob();
        checkBulkImageJob();

        // Polling loop for active jobs (in case they are restored while still processing)
        gmailIntervalRef.current = setInterval(checkGmailJob, POLL_INTERVAL_MS);
        bulkImageIntervalRef.current = setInterval(
            checkBulkImageJob,
            POLL_INTERVAL_MS
        );

        return () => {
            if (gmailIntervalRef.current) {
                clearInterval(gmailIntervalRef.current);
            }
            if (bulkImageIntervalRef.current) {
                clearInterval(bulkImageIntervalRef.current);
            }
        };
    }, [authHeader, dispatch]);
};

export default useImportJobsMonitor;
